import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SkillProgress from './SkillProgress';

interface Skill {
  name: string;
  level: number;
  icon: string;
  description: string;
}

interface SkillCategoryProps {
  category: string;
  skills: Skill[];
  viewMode: 'grid' | 'list';
  delay?: number;
  onChangeViewMode?: (mode: 'grid' | 'list') => void;
  showToggle?: boolean;
}

const SkillCategory: React.FC<SkillCategoryProps> = ({ category, skills, viewMode, delay = 0, onChangeViewMode, showToggle = false }) => {
  const { t } = useTranslation('skills');
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
      viewport={{ once: true }}
      className="mb-12"
    >
      {/* Category Header */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: delay + 0.2 }}
        viewport={{ once: true }}
        className="text-center mb-10 relative"
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold gradient-text mb-3">
          {t(`categories.${category}`, category)}
        </h2>
        <div className="w-20 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mx-auto" />
        {showToggle && onChangeViewMode && (
          <div className="absolute top-0 right-0">
            <div className="flex bg-dark-800/30 backdrop-blur-sm border border-primary-500/20 rounded-lg p-1">
              {[
                { value: 'grid', label: t('gridLabel'), icon: '⊞' },
                { value: 'list', label: t('listLabel'), icon: '☰' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => onChangeViewMode(option.value as 'grid' | 'list')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-300 flex items-center space-x-2 ${
                    viewMode === option.value
                      ? 'bg-primary-600 text-purple-200 shadow-md'
                      : 'text-dark-300 hover:text-primary-400 hover:bg-primary-500/10'
                  }`}
                >
                  <span>{option.icon}</span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Skills Display */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {skills.map((skill, index) => (
            <motion.div
              key={skill.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5, transition: { type: 'tween', duration: 0.2 } }}
              viewport={{ once: true }}
              className="group relative p-5 rounded-xl bg-dark-800/30 backdrop-blur-sm border border-dark-700/50 transition-all duration-300 hover:border-primary-500/50 hover:bg-dark-800/50"
            >
              <SkillProgress
                {...skill}
                delay={delay + index * 0.1}
              />
            </motion.div>
          ))}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="space-y-4 max-w-4xl mx-auto">
          {skills.map((skill, index) => (
            <motion.div
              key={skill.name}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              whileHover={{ y: -3, transition: { type: 'tween', duration: 0.2 } }}
              viewport={{ once: true }}
              className="group p-5 rounded-xl bg-dark-800/30 backdrop-blur-sm border border-dark-700/50 transition-all duration-300 hover:border-primary-500/50 hover:bg-dark-800/50"
            >
              <SkillProgress
                {...skill}
                delay={delay + index * 0.1}
              />
            </motion.div>
          ))}
        </div>
      )}

    </motion.div>
  );
};

export default SkillCategory;